import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    IconButton,
    CircularProgress
} from '@material-ui/core';
import { DragIndicator as DragIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useChallengeStore from '../../store/challengeStore';
import { useSearchChallengeDefinitions, useUpdateSortNumbers } from '../../hooks/useChallengeDefinitions';
import Swal from 'sweetalert2';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3),
        width: '100%',
        margin: 0
    },
    title: {
        // Removed - using inline style like Page2
    },
    warningMessage: {
        padding: theme.spacing(3),
        textAlign: 'center',
        backgroundColor: theme.palette.warning.light,
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2)
    },
    challengeItem: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: '1px solid #ff9800',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        cursor: 'move',
        boxShadow: '0 2px 4px rgba(255, 152, 0, 0.2)',
        transition: 'all 0.2s ease',
        '&:hover': {
            boxShadow: '0 4px 8px rgba(255, 152, 0, 0.3)',
            transform: 'translateY(-2px)'
        }
    },
    dragHandle: {
        cursor: 'grab',
        color: '#ff9800',
        '&:active': {
            cursor: 'grabbing'
        }
    },
    challengeTitle: {
        flex: 1,
        fontWeight: 500
    },
    saveButton: {
        marginTop: theme.spacing(3),
        backgroundColor: '#ff9800',
        color: '#fff',
        padding: theme.spacing(1.5, 4),
        fontSize: '1rem',
        fontWeight: 600,
        borderRadius: theme.spacing(1),
        textTransform: 'none',
        boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#e68900',
            boxShadow: '0 6px 16px rgba(255, 152, 0, 0.4)',
            transform: 'translateY(-2px)'
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.grey[500]
        }
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4)
    }
}));

const SortableChallengeItem = ({ challenge, index }) => {
    const classes = useStyles();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: challenge.key });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Box className={classes.challengeItem}>
                <IconButton
                    size="small"
                    className={classes.dragHandle}
                    {...attributes}
                    {...listeners}
                >
                    <DragIcon />
                </IconButton>
                <Typography className={classes.challengeTitle}>
                    {index + 1}. {challenge.challengeTitle}
                </Typography>
            </Box>
        </div>
    );
};

const Page3ChallengeOrdering = () => {
    const classes = useStyles();
    const { challengeId, challengeLearningTopic } = useChallengeStore();
    const [challenges, setChallenges] = useState([]);

    // Determina se la validazione passa - safe check
    const isValid = Boolean(challengeId) && Boolean(challengeLearningTopic?.trim());

    // Hook per cercare le challenge definitions
    const { data, isFetching, error } = useSearchChallengeDefinitions(
        challengeLearningTopic || '',
        isValid // enabled solo se la validazione passa
    );

    // Hook per aggiornare i sort numbers
    const updateSortNumbers = useUpdateSortNumbers();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8
            }
        })
    );

    // Aggiorna lo stato locale quando arrivano i dati
    useEffect(() => {
        if (data?.challengeDefinitions) {
            setChallenges(data.challengeDefinitions);
        }
    }, [data]);

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = challenges.findIndex((item) => item.key === active.id);
            const newIndex = challenges.findIndex((item) => item.key === over.id);
            const newChallenges = arrayMove(challenges, oldIndex, newIndex);
            setChallenges(newChallenges);
        }
    };

    const handleSaveOrder = async () => {
        // Build the payload with current order
        const challengeOrder = challenges.map((challenge, index) => ({
            key: challenge.key,
            sortNumber: index + 1
        }));

        try {
            await updateSortNumbers.mutateAsync(challengeOrder);

            // Show success popup
            Swal.fire({
                type: 'success',
                title: 'Order saved',
                showConfirmButton: false,
                timer: 1500
            });
        } catch (error) {
            // Show error popup
            Swal.fire({
                type: 'error',
                title: 'Error',
                text: 'Unable to save the order. Please try again.',
                showConfirmButton: true
            });
        }
    };

    // If validation fails, show only the message
    if (!isValid) {
        return (
            <Paper className={classes.container} elevation={2}>
                <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                    Challenge Ordering
                </Typography>
                <Box className={classes.warningMessage}>
                    <Typography variant="h6" gutterBottom>
                        To order challenges, save the challenge first
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Make sure you have entered the Learning Topic and saved the challenge at least once.
                    </Typography>
                </Box>
            </Paper>
        );
    }

    // Show loading
    if (isFetching) {
        return (
            <Paper className={classes.container} elevation={2}>
                <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                    Challenge Ordering for Learning Topic: '{challengeLearningTopic}'
                </Typography>
                <Box className={classes.loadingContainer}>
                    <CircularProgress style={{ color: '#ff9800' }} />
                </Box>
            </Paper>
        );
    }

    // Show error
    if (error) {
        return (
            <Paper className={classes.container} elevation={2}>
                <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                    Challenge Ordering for Learning Topic: '{challengeLearningTopic}'
                </Typography>
                <Box className={classes.warningMessage}>
                    <Typography variant="body1" color="error">
                        Error loading challenges: {error.message}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    // No challenges found
    if (!challenges || challenges.length === 0) {
        return (
            <Paper className={classes.container} elevation={2}>
                <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                    Challenge Ordering for Learning Topic: '{challengeLearningTopic}'
                </Typography>
                <Box className={classes.warningMessage}>
                    <Typography variant="body1">
                        No challenges found for this Learning Topic.
                    </Typography>
                </Box>
            </Paper>
        );
    }

    // Show the list with drag-and-drop
    return (
        <Paper className={classes.container} elevation={2}>
            <Typography variant="h5" gutterBottom style={{ marginBottom: 24 }}>
                Challenge Ordering for Learning Topic: '{challengeLearningTopic}'
            </Typography>

            <Box mt={3}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                <SortableContext
                    items={challenges.map(c => c.key)}
                    strategy={verticalListSortingStrategy}
                >
                    {challenges.map((challenge, index) => (
                        <SortableChallengeItem
                            key={challenge.key}
                            challenge={challenge}
                            index={index}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            </Box>

            <Button
                variant="contained"
                onClick={handleSaveOrder}
                disabled={updateSortNumbers.isLoading}
                className={classes.saveButton}
            >
                {updateSortNumbers.isLoading ? 'Saving...' : 'Save Order'}
            </Button>
        </Paper>
    );
};

export default Page3ChallengeOrdering;
